import React, { useMemo, useState } from "react";
import { Activity, TrendingUp } from "lucide-react";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import Card from "src/components/common/card/Card";
import CardHeader from "src/components/common/card/CardHeader";
import Skeleton from "src/components/ui/Skeleton";
import Button from "src/components/ui/Button";
import ContactSupportModal from "src/components/modals/ContactSupportModal";
import usePlanUsage from "src/hooks/usePlanUsage";
import { useAppSelector } from "src/redux/hooks";
import { formatDateTime } from "src/utils/date-time";

const UPGRADE_THRESHOLD = 90;

const MONTHLY_RESET_KEYS = new Set([
  "totalSaleOrders",
  "totalPurchaseOrders",
  "totalAdjustmentOrders",
]);

const barColor = (percent: number) => {
  if (percent >= 90) return "bg-red-500";
  if (percent >= 70) return "bg-amber-500";
  return "bg-primary-500";
};

const SettingsUsagePage: React.FC = () => {
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";

  const { planUsage, loading } = usePlanUsage({ enabled: isTenantAdmin });
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  const usageItems = useMemo(() => {
    const usage = planUsage?.usage;
    const limits = planUsage?.plan?.features;
    if (!usage || !limits) return [];

    return [
      { key: "totalUsers", label: "Staff" },
      { key: "totalProducts", label: "Products" },
      { key: "totalSuppliers", label: "Suppliers" },
      { key: "totalSaleOrders", label: "Sale Orders" },
      { key: "totalPurchaseOrders", label: "Purchase Orders" },
      { key: "totalAdjustmentOrders", label: "Adjustment Orders" },
    ].map((item) => {
      const used = usage[item.key as keyof typeof usage] ?? 0;
      const limit = limits[item.key as keyof typeof limits] ?? 0;
      const percent =
        limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
      return {
        ...item,
        used,
        limit,
        percent,
        monthly: MONTHLY_RESET_KEYS.has(item.key),
      };
    });
  }, [planUsage]);

  const resetsAt = formatDateTime(
    planUsage?.usagePeriodResetsAt || "",
    "DD MMM YYYY"
  );

  const nearLimitItems = useMemo(
    () => usageItems.filter((item) => item.percent >= UPGRADE_THRESHOLD),
    [usageItems]
  );

  return (
    <DashboardLayout
      meta={{
        title: "Usage | Invyntra",
        description: "Track usage against your workspace plan limits.",
        keywords: "usage, plan limits, settings, invyntra",
      }}
    >
      <div className="mx-auto max-w-3xl space-y-4">
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
            <Activity size={26} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Usage
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Usage against your current plan's limits.
            </p>
          </div>
        </div>

        {!isTenantAdmin ? (
          <Card>
            <div className="p-6 text-center text-sm text-gray-600 dark:text-gray-300">
              Only tenant admins can view usage statistics.
            </div>
          </Card>
        ) : (
          <Card>
            <CardHeader
              title="Usage statistics"
              description="Usage against plan limits."
            />
            <div className="space-y-5">
              {loading &&
                Array.from({ length: 6 }).map((_, idx) => (
                  <div key={`usage-skeleton-${idx}`} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-3.5 w-12" />
                    </div>
                    <Skeleton className="h-2.5 w-full rounded-full" />
                  </div>
                ))}
              {!loading && usageItems.length === 0 && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Plan usage details are not available.
                </div>
              )}
              {!loading &&
                usageItems.map((item) => (
                  <div key={item.key} className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700 dark:text-gray-300">
                        {item.label}
                        {item.monthly && (
                          <span className="ml-1.5 text-xs font-normal text-gray-400 dark:text-gray-500">
                            (this month)
                          </span>
                        )}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400">
                        {item.used} / {item.limit}
                        <span className="ml-1.5 text-xs text-gray-400 dark:text-gray-500">
                          ({item.percent}%)
                        </span>
                      </span>
                    </div>
                    <div className="h-2.5 w-full rounded-full bg-gray-100 dark:bg-gray-700">
                      <div
                        className={`h-2.5 rounded-full transition-all ${barColor(item.percent)}`}
                        style={{ width: `${Math.max(item.percent, item.used > 0 ? 3 : 0)}%` }}
                      />
                    </div>
                  </div>
                ))}
              {!loading && resetsAt && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Sale, purchase and adjustment order limits reset monthly —
                  next reset on {resetsAt}. Staff, product and supplier
                  limits apply for your whole plan period.
                </p>
              )}
            </div>
          </Card>
        )}

        {!loading && nearLimitItems.length > 0 && (
          <Card>
            <div className="flex flex-wrap items-center justify-between gap-4 p-1">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300">
                  <TrendingUp size={18} />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">
                    You're close to your plan's limit
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {nearLimitItems.map((item) => item.label).join(", ")}{" "}
                    {nearLimitItems.length > 1 ? "are" : "is"} at{" "}
                    {Math.max(...nearLimitItems.map((item) => item.percent))}%
                    or more. Upgrade to a higher plan to avoid interruptions.
                  </p>
                </div>
              </div>
              <Button onClick={() => setIsUpgradeModalOpen(true)}>
                Upgrade plan
              </Button>
            </div>
          </Card>
        )}
      </div>

      <ContactSupportModal
        open={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        title="Upgrade Plan"
      />
    </DashboardLayout>
  );
};

export default SettingsUsagePage;
